import { useLowerthirdEnabled } from '@app/modules/audit/hooks/useLowerthirdEnabled';
import { useYoutubeStatsEnabled } from '@app/modules/audit/hooks/useYoutubeStatsEnabled';
import { useAdoverlayEnabled } from '@app/modules/audit/modules/station-audit/hooks/useAdoverlayEnabled';
import TranscriptionsContainer from '@app/modules/logger/screens/stream/components/audit/transcriptions/transcriptions.container';
import { isTranscriptionEnabled } from '@app/modules/logger/screens/stream/diarization/isTranscriptionEnabled';
import { ParamDrivenTabsMui } from '@mi-front/ui';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useActiveStation } from '../../../../../../../core/stations/hooks/useActiveStation';
import { isAuditEnabled } from '../../../../../../audit/helpers';
import { useMentionsEnabledForActiveStation } from '../../../../../../audit/modules/station-audit/hooks/useMentionsEnabledForActiveStation';
import { WaveScheduleStatusView } from '../../../../../../audit/modules/station-audit/screens/audio-matches-list/WaveScheduleStatusView';
import { WaveMentionsStatusView } from '../../../../../../audit/modules/station-audit/screens/mentions-list/WaveMentionsStatusView';
import { useStreamSettingsStore } from '../../../stores/stream-screen-settings.store';
import AdoverlayContainer from '../adoverlay/adoverlay.container';
import LowerthirdContainer from '../lowerthird/LowerthirdContainer';
import { usePersonsEnabledForStation } from '../persons/hooks/usePersonsShow';
import { PersonsContainer } from '../persons/view/PersonsContainer';
import YoutubeStatsContainer from '../youtube-stats/YoutubeStatsContainer';

export const StreamAuditResults = () => {
  const { $t } = useIntl();
  const station = useActiveStation();
  const settingsAudit = useStreamSettingsStore((state) => state.audit);

  const liveMentionsEnabled =
    useMentionsEnabledForActiveStation() && settingsAudit.liveMentions && isAuditEnabled(station);
  const resultsEnabled = isAuditEnabled(station) && settingsAudit.matches;
  const transcriptionsEnabled = isTranscriptionEnabled(station);
  const personsEnabled = usePersonsEnabledForStation();
  const lowerthirdEnabled = useLowerthirdEnabled(station);
  const adoverlayEnabled = useAdoverlayEnabled(station);
  const ytstatsEnabled = useYoutubeStatsEnabled(station);

  const tabs = useMemo(() => {
    const tabsArray = [];
    if (resultsEnabled) {
      tabsArray.push({
        value: 'audiomatches',
        label: $t({
          id: 'audit.streamScreen.audioMatches',
          defaultMessage: 'Resultados',
        }),
        content: (
          <Box sx={{ mt: 3 }}>
            <WaveScheduleStatusView station={station} />
          </Box>
        ),
      });
    }
    if (liveMentionsEnabled) {
      tabsArray.push({
        value: 'livementions',
        label: $t({
          id: 'audit.streamScreen.liveMentions',
          defaultMessage: 'Menciones',
        }),
        content: (
          <Box sx={{ mt: 3 }}>
            <WaveMentionsStatusView station={station} />
          </Box>
        ),
      });
    }
    if (transcriptionsEnabled) {
      tabsArray.push({
        value: 'transcriptions',
        label: $t({
          id: 'audit.streamScreen.transcriptions',
          defaultMessage: 'Transcripciones',
        }),
        content: (
          <Box sx={{ mt: 3 }}>
            <TranscriptionsContainer station={station} />
          </Box>
        ),
      });
    }
    if (personsEnabled) {
      tabsArray.push({
        value: 'persons',
        label: $t({
          id: 'logger.persons.track.title',
          defaultMessage: 'Personas',
        }),
        content: (
          <Box sx={{ mt: 3 }}>
            <PersonsContainer />
          </Box>
        ),
      });
    }
    if (lowerthirdEnabled) {
      tabsArray.push({
        value: 'lowerthird',
        label: $t({
          id: 'audit.streamScreen.lowerthird',
          defaultMessage: 'Zocalos',
        }),
        content: (
          <Box sx={{ mt: 3 }}>
            <LowerthirdContainer station={station} />
          </Box>
        ),
      });
    }
    if (adoverlayEnabled) {
      tabsArray.push({
        value: 'adoverlay',
        label: $t({
          id: 'audit.streamScreen.adoverlay',
          defaultMessage: 'Ad Overlay',
        }),
        content: (
          <Box sx={{ mt: 3 }}>
            <AdoverlayContainer station={station} />
          </Box>
        ),
      });
    }
    if (ytstatsEnabled) {
      tabsArray.push({
        value: 'youtube-stats',
        label: 'YouTube Stats',
        content: (
          <Box sx={{ mt: 3 }}>
            <YoutubeStatsContainer />
          </Box>
        ),
      });
    }
    return tabsArray;
  }, [
    resultsEnabled,
    liveMentionsEnabled,
    transcriptionsEnabled,
    personsEnabled,
    lowerthirdEnabled,
    adoverlayEnabled,
    ytstatsEnabled,
    $t,
    station,
  ]);

  if (!tabs.length) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <ParamDrivenTabsMui id="group-view-tabs" tabs={tabs} tabPaddingTop={0} />
    </Box>
  );
};
